import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import FleetMap from "@/components/FleetMap";
import { Navigation, AlertTriangle, LocateFixed } from "lucide-react";
import logo from "@/assets/boomerang_logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InterventionPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [geoErr, setGeoErr] = useState(false);
  const posRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/intervention/${token}`);
      setState(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 410 ? "Ce lien d'intervention a expiré." : "Lien d'intervention invalide.");
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    let wid = null;
    if (navigator.geolocation) {
      wid = navigator.geolocation.watchPosition(
        (p) => {
          const pos = { lat: p.coords.latitude, lon: p.coords.longitude };
          posRef.current = pos;
          axios.post(`${BACKEND_URL}/api/public/intervention/${token}/ping`, pos).catch(() => {});
        },
        () => setGeoErr(true),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );
    } else {
      setGeoErr(true);
    }
    return () => { clearInterval(t); if (wid != null && navigator.geolocation) navigator.geolocation.clearWatch(wid); };
  }, [load, token]);

  const veh = state?.vehicle;
  const vehicles = veh?.last_position
    ? [{ id: "target", name: veh.name, plate: veh.plate, icon: veh.icon, color: veh.color, status: veh.status, last_position: veh.last_position }]
    : [];
  const userPos = state?.intervenant ? { lat: state.intervenant.lat, lon: state.intervenant.lon } : null;
  const routePath = state?.route?.geometry ? state.route.geometry.map(([lat, lon]) => ({ lat, lon })) : [];
  const routeDest = veh?.last_position ? { lat: veh.last_position.lat, lon: veh.last_position.lon, label: veh.name } : null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="h-14 glass flex items-center justify-between px-4 z-[1000]">
        <img src={logo} alt="Boomerang" className="h-7 w-auto object-contain" />
        {veh && <span className="text-sm font-semibold flex items-center gap-2" data-testid="intervention-title"><Navigation className="w-4 h-4 brand-teal" /> Rejoindre {veh.name}</span>}
      </header>

      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6" data-testid="intervention-error">
          <AlertTriangle className="w-10 h-10 text-[#F5821F]" />
          <p className="text-muted-foreground">{error}</p>
        </div>
      ) : (
        <div className="relative" style={{ height: "calc(100vh - 3.5rem)" }} data-testid="intervention-map">
          <FleetMap vehicles={vehicles} geofences={[]} selected={vehicles[0] || null} userPos={userPos} routePath={routePath} routeDest={routeDest} fitAll={!routePath.length} />
          {geoErr && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 glass rounded-lg px-4 py-2 z-[600] text-xs text-[#F5821F] flex items-center gap-2" data-testid="intervention-geoerr">
              <LocateFixed className="w-4 h-4" /> Autorisez la localisation pour être guidé
            </div>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass rounded-lg px-4 py-2 z-[600] flex items-center gap-4 data text-xs" data-testid="intervention-info">
            {state?.route ? (
              <>
                <span className="flex items-center gap-1.5 text-[#F5821F] font-semibold"><Navigation className="w-3.5 h-3.5" /> {state.route.distance_km} km · ~{state.route.duration_min} min</span>
                <span className="text-muted-foreground">Itinéraire live · maj 5s</span>
              </>
            ) : (
              <span className="text-muted-foreground">{userPos ? "Calcul de l'itinéraire…" : "En attente de votre position…"}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
